<script lang="ts" setup>
import type {VehicleIdentifierPlateSize} from '@/constants/vehicle-identifier-plate-size';
import { computed, onMounted, ref } from "vue";
import { Country } from "@/constants/country";
import { useMessage, type MessageApi } from "naive-ui";
import { useI18n } from "vue-i18n";

const SUPPORTED_COUNTRIES = [
  Country.NL,
  Country.BE,
  Country.DE,
  Country.FR,
  Country.LX,
];

interface Props {
  licensePlate?: string;
  size?: VehicleIdentifierPlateSize;
  country?: Country;
  readOnly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  size: "medium",
  country: Country.UNKNOWN,
  readOnly: false
});

const country = ref<Country>(Country.UNKNOWN);
const message: MessageApi | null = props.readOnly ? null : useMessage();
const { t } = useI18n();

onMounted(() => {
  if (props.country && !SUPPORTED_COUNTRIES.includes(props.country)) {
    country.value = Country.UNKNOWN;
  } else {
    country.value = props.country;
  }
});

const countryDisplayMap: Record<Country, string> = {
  [Country.NL]: "NL",
  [Country.BE]: "B",
  [Country.FR]: "F",
  [Country.DE]: "D",
  [Country.LX]: "L",
  [Country.UNKNOWN]: 'UNKNOWN'
};

const countryDisplayCode = computed(() => {
  if (country.value === Country.UNKNOWN) {
    return "";
  }

  return countryDisplayMap[country.value as Country] || "NL";
});

const copyLicensePlate = async () => {
  if (!props.licensePlate) {
    return;
  }

  try {
    await navigator.clipboard.writeText(props.licensePlate);
    message?.success(t('components.license_plate.copy.success'));
  } catch {
    message?.error(t('components.license_plate.copy.failed'));
  }
};

const handlePlateClick = (event: MouseEvent) => {
  if (props.readOnly) {
    return;
  }
  event.stopPropagation();
  event.preventDefault();
  copyLicensePlate();
};

const handlePlateKey = (event: KeyboardEvent) => {
  if (props.readOnly) {
    return;
  }
  event.stopPropagation();
  event.preventDefault();
  copyLicensePlate();
};
</script>

<template>
  <div
      v-if="props.licensePlate"
      class="vehicle-identification-plate-wrapper"
      :class="{
        'small': props.size === 'small',
        'large': props.size === 'large',
        'huge': props.size === 'huge',
        'be': country === Country.BE,
        'de': country === Country.DE,
        'fr': country === Country.FR,
        'unknown': country === Country.UNKNOWN,
        'read-only': props.readOnly,
      }"
      :role="props.readOnly ? undefined : 'button'"
      :tabindex="props.readOnly ? undefined : 0"
      @click="handlePlateClick"
      @keydown.enter="handlePlateKey"
      @keydown.space="handlePlateKey"
  >
    <div class="license-plate-country">{{ countryDisplayCode }}</div>
    <div class="vehicle-identification-plate">
      <div class="vehicle-identification-plate__character" v-for="character in props.licensePlate.split('')">
        {{ character }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.vehicle-identification-plate-wrapper {
  border: 1px solid var(--dis-license-plate-bordercolor);
  background-color: var(--dis-license-plate-backgroundcolor);
  box-shadow: 0 0 0 1px var(--dis-license-plate-backgroundcolor);
  text-transform: uppercase;
  cursor: pointer;
  user-select: none;

  &.read-only {
    cursor: inherit;
  }

  &:focus-visible {
    outline: 2px solid var(--dis-color-focus, currentColor);
    outline-offset: 2px;
  }

  &.unknown {
    background-color: var(--dis-license-plate-unknown-backgroundcolor);
    box-shadow: 0 0 0 1px var(--dis-license-plate-unknown-backgroundcolor);

    .license-plate-country {
      background-color: var(--dis-license-plate-unknown-countrybackgroundcolor);
      color: var(--dis-license-plate-unknown-countrytextcolor);
    }

    .vehicle-identification-plate {
      .vehicle-identification-plate__character {
        color: var(--dis-license-plate-unknown-textcolor);
      }
    }
  }

  &.be,
  &.de,
  &.fr {
    background-color: var(--dis-color-neutral-white);
    box-shadow: 0 0 0 1px var(--dis-color-neutral-white);

    .vehicle-identification-plate {
      .vehicle-identification-plate__character {
        color: var(--dis-color-neutral-black);
      }
    }
  }

  &.be {
    .vehicle-identification-plate {
      .vehicle-identification-plate__character {
        color: var(--dis-color-semantic-red-600);
      }
    }
  }

  .license-plate-country {
    flex: 0 0 auto;
    display: flex;
    width: 8px;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    align-self: stretch;
    background-color: var(--dis-license-plate-countrybackgroundcolor);
    color: var(--dis-license-plate-countrytextcolor);
    font-size: 5px;
    font-weight: var(--dis-font-weight-strong);
    letter-spacing: -0.3px;
    line-height: 1;
    padding-bottom: 2px;
    text-align: center;
  }

  .vehicle-identification-plate {
    .vehicle-identification-plate__character {
      color: var(--dis-license-plate-textcolor);
    }
  }

  &.huge {
    .license-plate-country {
      width: 12px;
      font-size: 8px;
      padding-bottom: 3px;
    }
  }
}
</style>
